import Axios from 'axios';
import {
    IServiceByDateRequest,
    IReportServiceByDateResponce,
    IInvoiceByDateRequest,
    IReportInvoiceByDateResponce,
    ICo2ReportResponseEntity
} from '@/domain/IReport';
import config from '../../config.js';
import store from '@/store';
import i18n from '../i18n';

export abstract class ReportApi {
    private static axios = Axios.create({
        baseURL: config.API_URL,
        headers: {
            common: {
                'Content-Type': 'application/json'
            }
        }
    });

    static async getServicesByDate(ServiceByDateRequest: IServiceByDateRequest): Promise<IReportServiceByDateResponce | null> {
        let toplevels = '';
        if (ServiceByDateRequest.toplevel.length > 0) {
            toplevels = ServiceByDateRequest.toplevel.map((a) => a.toplevel_id).toString();
        }
        let url =
            '/servicebydate?servicestartdate=' +
            ServiceByDateRequest.service.servicestartdate +
            '&serviceenddate=' +
            ServiceByDateRequest.service.serviceenddate +
            '&invoicedservicesonly=' +
            ServiceByDateRequest.invoicedServices +
            '&lang=' +
            i18n.locale +
            '&customerid=' +
            ServiceByDateRequest.customerid;
        if (ServiceByDateRequest.service.servicedestinationcountry !== '') {
            url = url + '&servicedestinationcountry=' + ServiceByDateRequest.service.servicedestinationcountry;
        }
        if (ServiceByDateRequest.passenger.name !== '') {
            url = url + '&passengername=' + ServiceByDateRequest.passenger.name;
        }
        if (ServiceByDateRequest.passenger.reference !== '') {
            url = url + '&passengerreference=' + ServiceByDateRequest.passenger.reference;
        }
        if (ServiceByDateRequest.booking.employeenumber !== '') {
            url = url + '&bookingemployeenumber=' + ServiceByDateRequest.booking.employeenumber;
        }
        if (ServiceByDateRequest.booking.projectcode !== '') {
            url = url + '&bookingprojectcode=' + ServiceByDateRequest.booking.projectcode;
        }
        if (ServiceByDateRequest.booking.budgetcode !== '') {
            url = url + '&bookingbudgetcode=' + ServiceByDateRequest.booking.budgetcode;
        }
        if (ServiceByDateRequest.ticket.ticketNo !== '') {
            url = url + '&ticketnumber=' + ServiceByDateRequest.ticket.ticketNo;
        }
        if (ServiceByDateRequest.booking.customerreference !== '') {
            url = url + '&customerreference=' + ServiceByDateRequest.booking.customerreference;
        }
        if (ServiceByDateRequest.booking.finaccount !== '') {
            url = url + '&finaccount=' + ServiceByDateRequest.booking.finaccount;
        }
        if (ServiceByDateRequest.booking.menumber !== '') {
            url = url + '&menumber=' + ServiceByDateRequest.booking.menumber;
        }
        if (ServiceByDateRequest.booking.bookedbyname !== '') {
            url = url + '&bookername=' + ServiceByDateRequest.booking.bookedbyname;
        }
        if (ServiceByDateRequest.saleschannels !== '') {
            url = url + '&saleschannels=' + ServiceByDateRequest.saleschannels;
        }
        if (ServiceByDateRequest.datetype !== '') {
            url = url + '&datetype=' + ServiceByDateRequest.datetype;
        }
        if (toplevels !== '') {
            url = url + '&toplevels=' + toplevels;
        }
        const encodedToken = store.state.token;
        const mydata = { services: null };
        try {
            const response = await this.axios.get<typeof mydata>(url, { headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken } });
            if (response.status === 200) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async getInvoicesByDate(InvoiceByDateRequest: IInvoiceByDateRequest): Promise<IReportInvoiceByDateResponce | null> {
        let url =
            '/invoices?invoicestartdate=' +
            InvoiceByDateRequest.invoicestartdate +
            '&invoiceenddate=' +
            InvoiceByDateRequest.invoiceenddate +
            '&lang=' +
            i18n.locale +
            '&customerid=' +
            InvoiceByDateRequest.customerid;
        if (InvoiceByDateRequest.passenger.name !== '') {
            url = url + '&passengername=' + InvoiceByDateRequest.passenger.name;
        }
        if (InvoiceByDateRequest.invoicenumber !== '') {
            url = url + '&invoicenumber=' + InvoiceByDateRequest.invoicenumber;
        }
        if (InvoiceByDateRequest.paymentstatus !== 'All') {
            url = url + '&paymentstatus=' + InvoiceByDateRequest.paymentstatus;
        }
        if (InvoiceByDateRequest.passenger.reference !== '') {
            url = url + '&passengerreference=' + InvoiceByDateRequest.passenger.reference;
        }
        if (InvoiceByDateRequest.booking.bookedbyname !== '') {
            url = url + '&bookername=' + InvoiceByDateRequest.booking.bookedbyname;
        }
        if (InvoiceByDateRequest.booking.customerreference !== '') {
            url = url + '&customerreference=' + InvoiceByDateRequest.booking.customerreference;
        }
        if (InvoiceByDateRequest.booking.projectcode !== '') {
            url = url + '&projectcode=' + InvoiceByDateRequest.booking.projectcode;
        }
        if (InvoiceByDateRequest.booking.menumber !== '') {
            url = url + '&menumber=' + InvoiceByDateRequest.booking.menumber;
        }
        if (InvoiceByDateRequest.booking.finaccount !== '') {
            url = url + '&finaccount=' + InvoiceByDateRequest.booking.finaccount;
        }
        if (InvoiceByDateRequest.booking.budgetcode !== '') {
            url = url + '&budgetcode=' + InvoiceByDateRequest.booking.budgetcode;
        }

        const encodedToken = store.state.token;
        const mydata = { invoiceextrafields: false, invoicelist: [] };
        try {
            const response = await this.axios.get<typeof mydata>(url, { headers: { 'Authentication-Estravel': `Bearer ${encodedToken}` } });
            if (response.status === 200) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }

    static async getCo2ReportByDate(ServiceByDateRequest: Partial<IServiceByDateRequest>): Promise<ICo2ReportResponseEntity[] | null> {
        const url =
            '/co2_report?date_from=' +
            ServiceByDateRequest.service.servicestartdate +
            '&date_to=' +
            ServiceByDateRequest.service.serviceenddate +
            '&customerid=' +
            ServiceByDateRequest.customerid;
        const encodedToken = store.state.token;
        try {
            const response = await this.axios({
                method: 'post',
                url,
                headers: { 'Authentication-Estravel': 'Bearer ' + encodedToken }
            });
            if (response.status === 200) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('error: ', (error as Error).message);
            return null;
        }
    }
}
